import { computed, reactive, watch } from 'vue'
import WaypointImage, {
  formatImageId,
  getImageSpacing,
} from '@/lib/waypoint-image'
import markup from '@/lib/markup'
import icons from '@/lib/icons'
import Color from '@/lib/color'
import { MARKUP_COLOR_DB_VALUE_MAP } from '@/lib/markup-colors'

import { addImage } from '@/services/map-service'
import { captureMarkupError } from '@/services/markup-service/helpers'

/**
 * A service for caching and generating waypoint images for use in an OpenLayers
 * map.
 */
class WaypointImageService {
  /**
   * Initializes a waypoint image service.
   *
   * @constructor
   */
  constructor() {
    /* Image cache as a look-up tree first keyed by icon, then color, then
       highlighted state.
       Lookup as: `this.cache[icon][color][highlightedKey]`. */
    this._cache = {
      // [icon]: {
      //   [color]: {
      //     [highlightedKey]: WaypointImage#instance
      //   }
      // }
    }
  }

  /**
   * Start watcher for new waypoint images being generated.
   * Watcher will add image to the map service image state for updating.
   */
  init() {
    const computedImages = computed(() => this.getImages())

    watch(
      computedImages,
      (imagesData) => {
        imagesData.forEach(async (imageObj) => {
          const { icon, color, highlighted, lowZoom, image } = imageObj
          const imageId = formatImageId(icon, color, highlighted, lowZoom)
          const { width, height, binary: data } = await image

          addImage(imageId, { width, height, data })
        })
      },
      { deep: true }
    )
  }

  /**
   * Returns a previously-generated image from the cache or generates a new one.
   *
   * @param {String} icon - The icon code for this image.
   * @param {Color} color - The color for this image.
   * @param {String} type - The background shape type for this image.
   * @returns {Promise} The WaypointImage generated or from the cache.
   */
  getImageOrGenerate(icon, color, type = 'normal') {
    const colorKey = color.toString()
    let image = this._getImage(icon, colorKey, type)

    if (image === null) {
      image = this._generateImage(icon, color, type)
      this._saveImage(icon, colorKey, type, image)
    }

    return image
  }

  /**
   * Returns the cached images along with some information about them.
   * @return {Array<Object>} Waypoint images.
   */
  getImages() {
    const images = []

    Object.keys(this._cache).forEach((icon) => {
      Object.keys(this._cache[icon]).forEach((color) => {
        Object.keys(this._cache[icon][color]).forEach((type) => {
          images.push({
            icon,
            color: Color.fromString(color),
            highlighted: type === 'highlighted',
            lowZoom: type === 'lowZoom',
            image: this._cache[icon][color][type],
          })
        })
      })
    })

    return images
  }

  /**
   * Gets an image from the cache by icon, color and type.
   *
   * @private
   * @param {String} icon - The icon code for this image.
   * @param {String} color - The stringified color code for this image.
   * @param {String} type - Type image key.
   * @returns {WaypointImage|null} Either the waypoint image, or null if an
   * image is not found.
   */
  _getImage(icon, color, type) {
    return this._cache[icon]?.[color]?.[type] || null
  }

  /**
   * Saves an image to the cache by icon and color.
   *
   * @private
   * @param {String} icon - The icon code for this image.
   * @param {String} color - The stringified color code for this image.
   * @param {String} type - The type string for this image.
   * @param {WaypointImage} image - The waypoint image to save.
   */
  _saveImage(icon, color, type, image) {
    if (!this._cache[icon]) {
      this._cache[icon] = {}
    }

    if (!this._cache[icon][color]) {
      this._cache[icon][color] = {}
    }
    this._cache[icon][color][type] = image
  }

  /**
   * Generates a new image and saves it to the image cache.
   *
   * @private
   * @async
   * @param {String} icon - Icon name.
   * @param {Color} color - Image color.
   * @param {String} type - Background shape type.
   */
  async _generateImage(icon, color, type) {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')

    // Draw background onto canvas.
    const backgroundImg = document.createElement('img')
    await new Promise((resolve) => {
      backgroundImg.onload = function () {
        canvas.width = this.width
        canvas.height = this.height
        context.drawImage(backgroundImg, 0, 0)
        resolve()
      }

      backgroundImg.onerror = function () {
        const error = new Error(
          `Failed to load waypoint background image (color: ${color}, type: ${type})`
        )
        captureMarkupError(error, 'waypoint_background_image_failed', {
          icon,
          color: color.toString(),
          type,
          src: backgroundImg.src,
        })
        resolve()
      }

      const colorDefinition = MARKUP_COLOR_DB_VALUE_MAP.get(color.toString())
      if (colorDefinition) {
        let iconSvg = colorDefinition.waypointSvg
        if (type === 'lowZoom') {
          iconSvg = colorDefinition.waypointLowZoomSvg
        } else if (type === 'highlighted') {
          iconSvg = colorDefinition.waypointSelectedSvg
        }
        backgroundImg.src = markup.svgIconString(iconSvg)
      } else {
        captureMarkupError(
          'No color definition',
          'waypoint_color_parsing_failed',
          { icon, color: color.toString(), type }
        )
        resolve()
      }
    })

    const svgs = icons.getAppIconSet()
    // fallback to generic 'Location' icon if unknown icon type
    const iconToDraw = svgs[icon] || svgs['Location']

    // Draw icon onto canvas.
    const iconImg = document.createElement('img')
    await new Promise((resolve) => {
      iconImg.onload = function () {
        const { dx, dy, x, y } = getImageSpacing(iconToDraw, canvas.width)

        context.drawImage(iconImg, dx, dy, x, y)
        resolve()
      }

      iconImg.onerror = function (e) {
        captureMarkupError(e, 'waypoint_icon_image_failed', {
          icon,
          color: color.toString(),
          type,
        })
        resolve()
      }

      iconImg.src = markup.svgIconString(iconToDraw)
    })

    return new WaypointImage(
      canvas.width,
      canvas.height,
      canvas.toDataURL(),
      context.getImageData(0, 0, canvas.width, canvas.height).data
    )
  }
}

export default reactive(new WaypointImageService())
